import React, { useState } from 'react';
import {
  AppBar, Toolbar, Typography, Drawer, IconButton,
  Box, useTheme, useMediaQuery, List, ListItem,
  ListItemIcon, ListItemText, Avatar, Divider, Card, CardContent,
} from '@mui/material';
import { Menu as MenuIcon, Logout as LogoutIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useStudentAuth } from '../../hooks/useStudentAuth';
import { studentMenuItems } from './studentMenu';

const drawerWidth = 280;

const StudentLayout = ({ children, currentPage, onSelectPage }) => {
  const theme                     = useTheme();
  const isMobile                  = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { student, logout }       = useStudentAuth();
  const navigate                  = useNavigate();

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen((p) => !p);
    } else {
      setSidebarOpen((p) => !p);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/student/login');
  };

  const initials = student
    ? `${student.first_name?.[0] ?? ''}${student.last_name?.[0] ?? ''}`.toUpperCase()
    : 'S';


  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar sx={{ minHeight: '56px !important', display: 'flex', alignItems: 'center', gap: 1 }}>
        <img src="/Logo.png" alt="School Sync" style={{ height: '32px', width: 'auto' }} />
        <Typography variant="h6" noWrap color="primary" sx={{ fontSize: '1rem', fontWeight: 600 }}>
          Student Portal
        </Typography>
      </Toolbar>

      {/* Student identity card */}
      <Box sx={{ px: 1.5, pb: 1 }}>
        <Card variant="outlined" sx={{ bgcolor: '#f1f8f1', borderRadius: 2, border: '1px solid #c8e6c9' }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Box>
              <Typography
                variant="body2"
                fontWeight={700}
                noWrap
                sx={{ color: 'primary.dark', fontSize: '0.8rem' }}
              >
                {student?.full_name}
              </Typography>
              <Typography
                variant="caption"
                noWrap
                sx={{ color: 'text.secondary', fontSize: '0.7rem', display: 'block' }}
              >
                {student?.registration_no}
              </Typography>
              {student?.class_stream && (
                <Typography
                  variant="caption"
                  noWrap
                  sx={{ color: 'text.secondary', fontSize: '0.68rem', display: 'block' }}
                >
                  {student.class_stream}
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>

      <List sx={{ flexGrow: 1, px: 1 }}>
        {studentMenuItems.map((item) => {
          const isActive      = currentPage === item.id;
          const IconComponent = item.icon;
          return (
            <ListItem
              key={item.id}
              component="div"
              onClick={() => { onSelectPage(item.id); if (isMobile) setMobileOpen(false); }}
              sx={{
                py: 0.6,
                px: 1.5,
                mb: 0.25,
                cursor: 'pointer',
                borderRadius: 2,
                bgcolor: isActive ? '#e8f5e9' : 'transparent',
                transition: 'background-color 0.15s, box-shadow 0.15s',
                '&:hover': {
                  bgcolor: isActive ? '#e8f5e9' : '#f5f5f5',
                  '& .MuiListItemIcon-root': { color: 'primary.main' },
                  '& .MuiTypography-root':   { color: 'primary.main' },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 34, color: isActive ? 'primary.main' : 'text.secondary', transition: 'color 0.15s' }}>
                {IconComponent && <IconComponent sx={{ fontSize: 18 }} />}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: '0.8rem',
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? 'primary.main' : 'text.primary',
                  transition: 'color 0.15s',
                }}
              />
              {isActive && (
                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'primary.main' }} />
              )}
            </ListItem>
          );
        })}
      </List>

      <Divider />
      <List>
        <ListItem
          component="div"
          onClick={handleLogout}
          sx={{
            py: 0.5,
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'transparent',
              '& .MuiListItemIcon-root': { color: 'error.main' },
              '& .MuiTypography-root':   { color: 'error.main' },
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>
            <LogoutIcon sx={{ fontSize: 18 }} />
          </ListItemIcon>
          <ListItemText
            primary="Logout"
            primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 500 }}
          />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { xs: '100%', md: sidebarOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          ml:    { xs: 0,      md: sidebarOpen ? `${drawerWidth}px` : 0 },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar>
          <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2 }}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ flexGrow: 1, fontSize: '1rem' }}>
            {student?.school_name || 'Student Portal'}
          </Typography>
          <Avatar
              src={student?.picture_url || undefined}
              sx={{ width: 32, height: 32, bgcolor: '#e3f2fd', color: 'primary.main', fontSize: 13, fontWeight: 600 }}
            >
              {!student?.picture_url && initials}
            </Avatar>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{
          width: { xs: 0, md: sidebarOpen ? drawerWidth : 0 },
          flexShrink: { md: 0 },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, display: 'flex', flexDirection: 'column' },
          }}
        >
          {drawerContent}
        </Drawer>
        <Drawer
          variant="persistent"
          open={sidebarOpen}
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, display: 'flex', flexDirection: 'column' },
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2, md: 3 },
          width: { xs: '100%', md: sidebarOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          mt: { xs: 7, md: 8 },
          minHeight: '100vh',
          backgroundColor: '#f5f5f5',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default StudentLayout;
